import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { authService } from '../services/authService';
import { USER_TYPES, DEFAULT_BRAND } from '../constants/constants';
import { useUI } from './UIContext';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passedMfa, setPassedMfa] = useState(false);
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const { setReady } = useUI();

  // Role-based access helpers
  const isMasterAdmin = useMemo(() => user?.userType === USER_TYPES.MASTER_ADMIN, [user]);
  const isUserManager = useMemo(() => user?.userType === USER_TYPES.USER_MANAGER || isMasterAdmin, [user, isMasterAdmin]);
  const isAdmin = useMemo(() => user?.userType === USER_TYPES.ADMIN || isUserManager, [user, isUserManager]);
  const isInternalUser = useMemo(() => user?.internalUser, [user]);

  // Brand helper
  const isCNG = useMemo(() => brand?.code === 'CNGC', [brand]);

  const resolveBrandFromApi = useCallback((response) => {
    if (!response || typeof response !== 'object') {
      return DEFAULT_BRAND;
    }

    const shortName = (response.shortName || DEFAULT_BRAND.shortName).toLowerCase();
    const code = response.code || DEFAULT_BRAND.code;

    // Backend is source-of-truth; DEFAULT_BRAND only fills gaps during transition.
    return {
      ...DEFAULT_BRAND,
      ...response,
      id: response.id || code.toLowerCase(),
      code,
      shortName,
      theme: response.theme || shortName,
      logoUrl: response.logoUrl || DEFAULT_BRAND.logoUrl,
    };
  }, []);

  const applyBrandConfig = useCallback((apiBrand) => {
    const brandConfig = resolveBrandFromApi(apiBrand);
    setBrand(brandConfig);
    document.documentElement.setAttribute('data-brand', brandConfig.theme);
  }, [resolveBrandFromApi]);

  // Fetch brand from API; use DEFAULT_BRAND only as bootstrap/fallback.
  const fetchBrand = useCallback(async () => {
    try {
      const response = await authService.getBrand();
      applyBrandConfig(response);
    } catch (error) {
      console.error('Failed to fetch brand:', error);
      // Keep current/default brand on error
    }
  }, [applyBrandConfig]);

  useEffect(() => {
    const initialize = async () => {
      try {
        const session = await authService.getSession();
        setUser(session.user);
        setIsAuthenticated(session.authenticated);
        setPassedMfa(session.passedMfa);

        if (session.brand) {
          applyBrandConfig(session.brand);
        } else {
          await fetchBrand();
        }
      } catch (error) {
        setUser(null);
        setIsAuthenticated(false);
        setPassedMfa(false);
      } finally {
        setReady();
      }
    };

    initialize();
  }, [setReady, fetchBrand, applyBrandConfig]);

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);
      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        setPassedMfa(!response.requiresMfa);
        // Re-fetch brand after login in case the resolved brand changed.
        await fetchBrand();
      }
      return response;
    } catch (error) {
      // Spring Security failure handler returns 401 with a LoginResponse JSON body.
      // Surface the structured response so LoginPage can show the message inline.
      if (error.response?.status === 401 && error.response.data) {
        return error.response.data;
      }
      throw error;
    }
  };

  const refreshSession = useCallback(async () => {
    const session = await authService.getSession();
    setUser(session.user);
    setIsAuthenticated(session.authenticated);
    setPassedMfa(session.passedMfa);

    if (session.brand) {
      applyBrandConfig(session.brand);
    }

    return session;
  }, [applyBrandConfig]);

  const verifyMfa = async (code, trustDevice) => {
    const response = await authService.verifyMfa(code, trustDevice);
    if (response.success) {
      setPassedMfa(true);
    }
    return response;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setPassedMfa(false);
    // Reset brand to default on logout
    setBrand(DEFAULT_BRAND);
    document.documentElement.setAttribute('data-brand', DEFAULT_BRAND.theme);
    // Preemptively reload live brand so seal is available on login page
    await fetchBrand();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        passedMfa,
        isMasterAdmin,
        isUserManager,
        isAdmin,
        isInternalUser,
        brand,
        isCNG,
        login,
        verifyMfa,
        logout,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook for accessing auth context.
 * @returns {Object} Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Custom hook for accessing brand from auth context.
 * Convenience wrapper for components that only need brand info.
 * @returns {Object} Brand context value { brand, isCNG }
 */
export const useBrand = () => {
  const { brand, isCNG } = useAuth();
  return { brand, isCNG };
};
